/**
 * The prompt-budget dimension of the assistant benchmark, as a wire contract in
 * `@orn/global`. It is a self-contained mirror of `@orn/api`'s in-memory
 * `ISurfaceMeasurement` (the static system-prompt + tool-registry token
 * measurement): the producer (`@orn/api`'s gate) projects each measured tree
 * into an {@link IMopsPromptBudgetArmV1}, and the consumers (`@orn/mops-api`
 * store, `@orn/mops-web` render) read it back.
 *
 * Why not import `ISurfaceMeasurement` directly: it types `channel` as
 * `AssistantChannel`, a `@orn/core` DB enum that this leaf package cannot
 * depend on. So `channel` / `primaryChannel` are plain `string` here — the
 * renderer only labels them, never switches on them — mirroring how
 * `MopsAssistantBenchmarkArtifact.ts` defines its own string unions rather than
 * importing the gate's types.
 */

/**
 * Soft ceiling for the static prefix as a fraction of the model's context
 * window — a fifth. Shared by the PR-comment renderer, the projector, and the
 * mops verdict view so the "over target" line reads from one number. Output /
 * thinking bill against a separate budget, so they are not folded in here.
 */
export const BUDGET_FRACTION = 0.2;

/** One system-prompt module's token contribution. */
export interface IMopsPromptBudgetSectionV1 {
  name: string;
  tokens: number;
}

/** One channel's assembled system-prompt total. `channel` is a label (`app` / `sms` / `email` / `headless`). */
export interface IMopsPromptBudgetChannelV1 {
  channel: string;
  tokens: number;
}

/** One parameter name's schema cost rolled up across every tool that carries it. */
export interface IMopsPromptBudgetParamV1 {
  name: string;
  tokens: number;
  toolCount: number;
}

/** The tool-registry measurement: aggregate token costs plus the heaviest parameters. */
export interface IMopsPromptBudgetToolsV1 {
  toolCount: number;
  nameTokens: number;
  descriptionTokens: number;
  parameterTokens: number;
  totalTokens: number;
  topParams: IMopsPromptBudgetParamV1[];
}

/** The static-prefix measurement for one commit (base or head). */
export interface IMopsPromptBudgetArmV1 {
  /** e.g. `chars/4`. */
  estimator: string;
  /** `provider/id`. */
  model: string;
  /** The model's input context window (the budget denominator); `0` when it couldn't be resolved. */
  contextWindowTokens: number;
  /** The channel the headline number + per-module breakdown are measured on. */
  primaryChannel: string;
  /** Assembled system-prompt total on the primary channel. */
  systemPromptTotalTokens: number;
  /** Per-module contributions on the primary channel, descending. */
  sections: IMopsPromptBudgetSectionV1[];
  /** Assembled system-prompt totals per measured channel. */
  byChannel: IMopsPromptBudgetChannelV1[];
  /** Tool-registry token breakdown (count, descriptions, parameters, heaviest params). */
  tools: IMopsPromptBudgetToolsV1;
  /** Headline budget number: primary system-prompt total + tool-registry total. */
  combinedTokens: number;
}

/**
 * Base→head static-prefix comparison. `base` is null when the base commit
 * predates the measure code (or measuring it failed), degrading the view to
 * head-only — the same shape the PR comment degrades to.
 */
export interface IMopsAssistantBenchmarkPromptBudgetV1 {
  base: IMopsPromptBudgetArmV1 | null;
  head: IMopsPromptBudgetArmV1;
}
